/**
 * Config Diff Page (Super Admin)
 *
 * Cross-clinic comparison for Programs, Pharmacies, and Physicians.
 * List-based layout that scales to many clinics. Expanding a row shows
 * per-clinic cards with field details and copy actions.
 */

import { useState, useEffect, useCallback } from 'react'
import api, { adminAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button
} from '../components/ui'
import { RefreshCw, Loader2, AlertCircle, Check, X, Minus, Filter, Copy, ChevronDown, ChevronRight } from 'lucide-react'

function formatCurrency(dollars) {
  if (dollars === null || dollars === undefined || isNaN(dollars)) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(dollars)
}

const TABS = [
  { value: 'programs', label: 'Drug Programs' },
  { value: 'pharmacies', label: 'Pharmacies' },
  { value: 'physicians', label: 'Physicians' },
]

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'drifted', label: 'Drifted Only' },
  { value: 'missing', label: 'Not in All Clinics' },
  { value: 'shared', label: 'Shared (2+ clinics)' },
]

export default function ConfigDiffPage() {
  const [activeTab, setActiveTab] = useState('programs')
  const [programData, setProgramData] = useState(null)
  const [pharmacyData, setPharmacyData] = useState(null)
  const [physicianData, setPhysicianData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all')
  const [expandedRows, setExpandedRows] = useState(new Set())

  const fetchTab = useCallback(async (tab) => {
    try {
      setLoading(true)
      setError(null)
      setExpandedRows(new Set())

      if (tab === 'programs') {
        if (programData) { setLoading(false); return }
        const response = await adminAPI.getConfigDiff()
        if (response.data.success) setProgramData(response.data.data)
      } else if (tab === 'pharmacies') {
        if (pharmacyData) { setLoading(false); return }
        const response = await adminAPI.getPharmacyDiff()
        if (response.data.success) setPharmacyData(response.data.data)
      } else if (tab === 'physicians') {
        if (physicianData) { setLoading(false); return }
        const response = await adminAPI.getPhysicianDiff()
        if (response.data.success) setPhysicianData(response.data.data)
      }
    } catch (err) {
      console.error('[CONFIG-DIFF] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [programData, pharmacyData, physicianData])

  useEffect(() => { fetchTab(activeTab) }, [activeTab, fetchTab])

  const refresh = useCallback(async () => {
    if (activeTab === 'programs') setProgramData(null)
    else if (activeTab === 'pharmacies') setPharmacyData(null)
    else if (activeTab === 'physicians') setPhysicianData(null)
  }, [activeTab])

  useEffect(() => {
    if (
      (activeTab === 'programs' && !programData) ||
      (activeTab === 'pharmacies' && !pharmacyData) ||
      (activeTab === 'physicians' && !physicianData)
    ) {
      fetchTab(activeTab)
    }
  }, [activeTab, programData, pharmacyData, physicianData, fetchTab])

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setFilter('all')
    setExpandedRows(new Set())
  }

  const toggleRow = (key) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const currentData = activeTab === 'programs' ? programData
    : activeTab === 'pharmacies' ? pharmacyData
    : physicianData

  const clinics = currentData?.clinics || []
  const summary = currentData?.summary

  const allItems = activeTab === 'programs'
    ? (currentData?.programs || [])
    : (currentData?.items || [])

  const filteredItems = allItems.filter(item => {
    if (filter === 'drifted') return item.drift.has_drift
    if (filter === 'missing') return item.present_in < clinics.length
    if (filter === 'shared') return item.present_in > 1
    return true
  })

  const entityLabel = activeTab === 'programs' ? 'Programs'
    : activeTab === 'pharmacies' ? 'Pharmacies'
    : 'Physicians'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Config Diff</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cross-clinic configuration comparison — {clinics.length} clinics
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span className="ml-1.5">Refresh</span>
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {TABS.map(tab => (
          <button
            key={tab.value}
            onClick={() => handleTabChange(tab.value)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.value
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <Card><CardContent className="py-6">
          <div className="text-center">
            <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
            <p className="text-destructive">{error}</p>
            <Button variant="outline" onClick={refresh} className="mt-3">Try Again</Button>
          </div>
        </CardContent></Card>
      )}

      {loading && (
        <Card><CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
            <p>Comparing {entityLabel.toLowerCase()} across clinics...</p>
          </div>
        </CardContent></Card>
      )}

      {/* Summary Cards */}
      {!loading && !error && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <Card><CardContent className="py-4 px-4">
            <span className="text-xs text-muted-foreground">Clinics</span>
            <p className="text-2xl font-bold">{clinics.length}</p>
          </CardContent></Card>
          <Card><CardContent className="py-4 px-4">
            <span className="text-xs text-muted-foreground">Total {entityLabel}</span>
            <p className="text-2xl font-bold">{summary.total_programs ?? summary.total}</p>
          </CardContent></Card>
          <Card><CardContent className="py-4 px-4">
            <span className="text-xs text-muted-foreground">Shared (2+)</span>
            <p className="text-2xl font-bold">{summary.shared_across_clinics}</p>
          </CardContent></Card>
          <Card><CardContent className="py-4 px-4">
            <span className="text-xs text-muted-foreground">With Drift</span>
            <p className={`text-2xl font-bold ${summary.drifted > 0 ? 'text-red-500' : 'text-green-600'}`}>{summary.drifted}</p>
          </CardContent></Card>
          <Card><CardContent className="py-4 px-4">
            <span className="text-xs text-muted-foreground">Single Clinic</span>
            <p className="text-2xl font-bold">{summary.single_clinic_only}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Filter */}
      {!loading && !error && currentData && (
        <Card><CardContent className="py-3 px-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <div className="flex gap-1">
              {FILTER_OPTIONS.map(opt => (
                <Button key={opt.value} variant={filter === opt.value ? 'default' : 'ghost'} size="sm"
                  onClick={() => setFilter(opt.value)} className="h-7 text-xs">
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent></Card>
      )}

      {/* Entity List */}
      {!loading && !error && filteredItems.length > 0 && (
        <div className="space-y-2">
          {filteredItems.map(item => {
            const key = activeTab === 'programs' ? item.drug_name
              : activeTab === 'pharmacies' ? item.ncpdpid
              : item.npi
            const label = activeTab === 'programs' ? item.drug_name
              : activeTab === 'pharmacies' ? item.pharmacy_name
              : item.name
            const sublabel = activeTab === 'programs'
              ? (item.entity_ids ? Object.values(item.entity_ids).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(', ') : '')
              : activeTab === 'pharmacies' ? `NCPDPID: ${item.ncpdpid}`
              : `NPI: ${item.npi}`
            const isExpanded = expandedRows.has(key)

            return (
              <Card key={key} className={item.drift.has_drift ? 'border-red-200 dark:border-red-900/30' : ''}>
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => toggleRow(key)}
                >
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}

                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{label}</div>
                    {sublabel && <div className="text-xs text-muted-foreground font-mono">{sublabel}</div>}
                  </div>

                  {/* Clinic presence badges */}
                  <div className="flex flex-wrap gap-1">
                    {clinics.map(c => {
                      const present = !!item.clinics[c.id]
                      return (
                        <span
                          key={c.id}
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            present
                              ? item.drift.has_drift
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-muted text-muted-foreground/50'
                          }`}
                          title={present ? `Present in ${c.name}` : `Absent from ${c.name}`}
                        >
                          {c.name}
                        </span>
                      )
                    })}
                  </div>

                  {/* Drift badge */}
                  <div className="flex-shrink-0">
                    {item.drift.has_drift ? (
                      <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-1.5 py-0.5 rounded">
                        {item.drift.fields.length} field{item.drift.fields.length !== 1 ? 's' : ''}
                      </span>
                    ) : item.present_in > 1 ? (
                      <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-1.5 py-0.5 rounded">
                        synced
                      </span>
                    ) : null}
                  </div>
                </div>

                {isExpanded && (
                  <CardContent className="pt-0 pb-4">
                    <div className="border-t pt-4">
                      {activeTab === 'programs' && <ProgramDetail item={item} clinics={clinics} />}
                      {activeTab === 'pharmacies' && <PharmacyDetail item={item} clinics={clinics} />}
                      {activeTab === 'physicians' && <PhysicianDetail item={item} clinics={clinics} />}
                    </div>
                  </CardContent>
                )}
              </Card>
            )
          })}
        </div>
      )}

      {!loading && !error && filteredItems.length === 0 && currentData && (
        <Card><CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            <p>No {entityLabel.toLowerCase()} match the current filter.</p>
          </div>
        </CardContent></Card>
      )}
    </div>
  )
}

// ── Per-clinic cards layout ──────────────────────────────────

function ClinicCards({ item, clinics, fields, children }) {
  const presentClinics = clinics.filter(c => item.clinics[c.id])
  const absentClinics = clinics.filter(c => !item.clinics[c.id])
  const driftedFields = new Set(item.drift.fields)

  return (
    <div className="space-y-4">
      {/* Field comparison across clinics */}
      {fields.length > 0 && presentClinics.length > 0 && (
        <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {presentClinics.map(clinic => {
            const cd = item.clinics[clinic.id]
            return (
              <div key={clinic.id} className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-semibold">{clinic.name}</h5>
                  <CopyDropdown item={item} fromClinic={clinic} allClinics={clinics} />
                </div>
                <div className="space-y-1">
                  {fields.map(f => {
                    const val = cd?.[f.key]
                    const display = f.format ? f.format(val) : (val ?? '-')
                    const isDrifted = driftedFields.has(f.key)
                    return (
                      <div key={f.key} className={`flex justify-between text-xs ${isDrifted ? 'bg-red-50/50 dark:bg-red-950/10 -mx-1 px-1 rounded' : ''}`}>
                        <span className="text-muted-foreground">{f.label}</span>
                        <span className={`font-mono ${isDrifted ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>{String(display)}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Absent clinics */}
      {absentClinics.length > 0 && (
        <div className="text-xs text-muted-foreground">
          <span className="font-medium">Not in: </span>
          {absentClinics.map(c => c.name).join(', ')}
        </div>
      )}

      {children}
    </div>
  )
}

// ── Copy dropdown ────────────────────────────────────────────

function CopyDropdown({ item, fromClinic, allClinics }) {
  const [open, setOpen] = useState(false)
  const [drafted, setDrafted] = useState(null)
  const [copying, setCopying] = useState(false)
  const user = useAuthStore(s => s.user)

  const targets = allClinics.filter(c => c.id !== fromClinic.id)

  const handleCopy = async (targetClinic) => {
    const userId = user?.id || user?._id
    if (!userId) return

    const sourceData = item.clinics[fromClinic.id]
    if (!sourceData) return

    const targetData = item.clinics[targetClinic.id] || {}
    const isNew = !item.clinics[targetClinic.id]
    const resourceType = item.ncpdpid ? 'pharmacy' : item.npi ? 'physician' : 'drug_program'

    setCopying(true)
    try {
      // Fetch the full source document from the source clinic
      let fullDoc
      const sourceId = sourceData._id
      if (!sourceId && !isNew) {
        alert('Source document ID not available for copy')
        return
      }

      if (sourceId) {
        // Fetch full document from the source clinic using source_clinic param
        const res = await api.get(
          resourceType === 'pharmacy' ? `/pharmacy/${sourceId}`
            : resourceType === 'physician' ? `/physicians/${sourceId}`
            : `/drug-programs/${sourceId}`,
          { params: { source_clinic: fromClinic.id } }
        )
        fullDoc = res.data?.data || res.data
      }

      if (!fullDoc) {
        alert('Failed to fetch source document')
        return
      }

      // Strip fields that shouldn't be copied
      const { _id, __v, created_at, updated_at, created_by, updated_by, ...cleanDoc } = fullDoc

      const draftChange = {
        id: crypto.randomUUID(),
        resource_type: resourceType,
        resource_id: isNew ? null : targetData._id || null,
        operation: isNew ? 'create' : 'update',
        original_data: isNew ? null : targetData,
        new_data: cleanDoc,
        timestamp: Date.now(),
        description: `${isNew ? 'Add' : 'Copy'} ${item.pharmacy_name || item.drug_name || item.name || ''} from ${fromClinic.name}`,
      }

      addDraftToClinic(targetClinic.id, userId, draftChange)
      setDrafted(targetClinic.id)
      setTimeout(() => setDrafted(null), 3000)
      setOpen(false)
    } catch (err) {
      console.error('[CONFIG-DIFF] Copy error:', err)
      alert(`Failed to copy: ${err.response?.data?.message || err.message}`)
    } finally {
      setCopying(false)
    }
  }

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
        disabled={copying}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/40 disabled:opacity-50"
      >
        {copying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Copy className="h-3 w-3" />}
        {copying ? 'Copying...' : 'Copy to...'}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-30 bg-background border rounded-lg shadow-lg py-1 min-w-[160px]">
            {targets.map(t => (
              <button
                key={t.id}
                onClick={(e) => { e.stopPropagation(); handleCopy(t) }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors flex items-center justify-between"
              >
                <span>{t.name}</span>
                {drafted === t.id && <Check className="h-3 w-3 text-green-600" />}
                {!item.clinics[t.id] && <span className="text-[10px] text-muted-foreground ml-1">(new)</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function addDraftToClinic(clinicId, userId, change) {
  const storageKey = `drafts_${clinicId}_${userId}`
  let existing = { changes: [], last_modified: Date.now() }
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) existing = JSON.parse(stored)
    if (!Array.isArray(existing.changes)) existing.changes = []
  } catch {}

  existing.changes = existing.changes.filter(c =>
    !(c.resource_type === change.resource_type && c.resource_id === change.resource_id)
  )

  existing.changes.push(change)
  existing.last_modified = Date.now()
  localStorage.setItem(storageKey, JSON.stringify(existing))
}

// ── Program detail ──────────────────────────────────────────

function ProgramDetail({ item, clinics }) {
  const fields = [
    { key: 'entity_id', label: 'Entity ID' },
    { key: 'pharmacy_count', label: 'Pharmacy Count' },
    { key: 'avg_ingredient_price', label: 'Avg Ingredient $', format: formatCurrency },
    { key: 'avg_shipping_price', label: 'Avg Shipping $', format: formatCurrency },
    { key: 'dea', label: 'DEA Required', format: v => v ? 'Yes' : 'No' },
    { key: 'local_pharmacy', label: 'Local Pharmacy', format: v => v ? 'Yes' : 'No' },
    { key: 'dosespot_type', label: 'DoseSpot Type' },
    { key: 'dosage_type', label: 'Dosage Type' },
    { key: 'dosage_period', label: 'Period' },
    { key: 'has_fill_schedule', label: 'Phased', format: v => v ? 'Yes' : 'No' },
  ]

  const presentClinics = clinics.filter(c => item.clinics[c.id])
  const driftedFields = new Set(item.drift.fields)

  return (
    <ClinicCards item={item} clinics={clinics} fields={fields}>
      {/* Pharmacy assignments */}
      {presentClinics.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">
            Pharmacy Assignments
            {driftedFields.has('pharmacy_names') && <span className="ml-2 text-red-500 font-normal">(differs)</span>}
          </h4>
          <div className="grid gap-2 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            {presentClinics.map(c => {
              const names = item.clinics[c.id]?.pharmacy_names || []
              return (
                <div key={c.id} className="border rounded-lg p-2">
                  <div className="text-xs font-semibold mb-1">{c.name}</div>
                  {names.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {names.map(n => <span key={n} className="inline-block bg-muted px-1.5 py-0.5 rounded text-[11px]">{n}</span>)}
                    </div>
                  ) : <span className="text-xs text-muted-foreground">None</span>}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </ClinicCards>
  )
}

// ── Pharmacy detail ─────────────────────────────────────────

const ALL_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DC','DE','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
]

function getDifferingStates(item, clinics, field) {
  const presentClinics = clinics.filter(c => item.clinics[c.id])
  if (presentClinics.length < 2) return new Set(ALL_STATES)
  const differing = new Set()
  const ref = new Set(item.clinics[presentClinics[0].id]?.[field] || [])
  for (let i = 1; i < presentClinics.length; i++) {
    const other = new Set(item.clinics[presentClinics[i].id]?.[field] || [])
    for (const st of ALL_STATES) {
      if (ref.has(st) !== other.has(st)) differing.add(st)
    }
  }
  return differing
}

function StateGrid({ states, label, color, differingStates }) {
  const stateSet = new Set(states || [])
  const visibleStates = differingStates ? ALL_STATES.filter(st => differingStates.has(st)) : ALL_STATES
  if (visibleStates.length === 0) return null
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground mb-1">
        {label} ({stateSet.size} total{differingStates ? `, ${visibleStates.length} differ` : ''})
      </div>
      <div className="flex flex-wrap gap-0.5">
        {visibleStates.map(st => (
          <span
            key={st}
            className={`inline-flex items-center justify-center w-7 h-5 text-[9px] font-mono rounded ${
              stateSet.has(st) ? `${color} font-semibold` : 'bg-muted/40 text-muted-foreground/30 line-through'
            }`}
            title={stateSet.has(st) ? `${st}: Licensed` : `${st}: Not licensed`}
          >
            {st}
          </span>
        ))}
      </div>
    </div>
  )
}

function PharmacyDetail({ item, clinics }) {
  const presentClinics = clinics.filter(c => item.clinics[c.id])
  const driftedFields = new Set(item.drift.fields)

  const basicFields = [
    { key: 'gateway', label: 'Gateway' },
    { key: 'enabled', label: 'Enabled', format: v => v ? 'Yes' : 'No' },
    { key: 'weekly_capacity', label: 'Weekly Cap' },
    { key: 'csv_upload', label: 'CSV Upload', format: v => v ? 'Yes' : 'No' },
    { key: 'state', label: 'Home State' },
  ]

  const diffControlled = driftedFields.has('states_controlled') ? getDifferingStates(item, clinics, 'states_controlled') : null
  const diffNonSterile = driftedFields.has('states_non_sterile') ? getDifferingStates(item, clinics, 'states_non_sterile') : null
  const diffSterile = driftedFields.has('states_sterile') ? getDifferingStates(item, clinics, 'states_sterile') : null
  const hasStateDrift = diffControlled || diffNonSterile || diffSterile

  return (
    <ClinicCards item={item} clinics={clinics} fields={basicFields}>
      {hasStateDrift && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">State Licensing Differences</h4>
          <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            {presentClinics.map(clinic => {
              const cd = item.clinics[clinic.id]
              return (
                <div key={clinic.id} className="border rounded-lg p-3">
                  <h5 className="text-xs font-semibold mb-2">{clinic.name}</h5>
                  <div className="space-y-2">
                    {diffControlled && diffControlled.size > 0 && (
                      <StateGrid states={cd.states_controlled} label="Controlled" color="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" differingStates={diffControlled} />
                    )}
                    {diffNonSterile && diffNonSterile.size > 0 && (
                      <StateGrid states={cd.states_non_sterile} label="Non-Sterile" color="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" differingStates={diffNonSterile} />
                    )}
                    {diffSterile && diffSterile.size > 0 && (
                      <StateGrid states={cd.states_sterile} label="Sterile" color="bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" differingStates={diffSterile} />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {!item.drift.has_drift && (
        <div className="text-xs text-muted-foreground">All config matches across clinics.</div>
      )}
    </ClinicCards>
  )
}

// ── Physician detail ────────────────────────────────────────

function PhysicianDetail({ item, clinics }) {
  const presentClinics = clinics.filter(c => item.clinics[c.id])
  const driftedFields = new Set(item.drift.fields)

  const basicFields = [
    { key: 'title', label: 'Title' },
    { key: 'prefix', label: 'Prefix' },
    { key: 'accept_new_patients', label: 'Accept New', format: v => v ? 'Yes' : 'No' },
    { key: 'accept_refill_patients', label: 'Accept Refill', format: v => v ? 'Yes' : 'No' },
    { key: 'minimize_scripts', label: 'Min Scripts', format: v => v ? 'Yes' : 'No' },
    { key: 'max_capacity', label: 'Max Cap', format: v => v === -1 ? 'Unlimited' : v },
  ]

  const diffLicensed = driftedFields.has('licensed_states') ? getDifferingStates(item, clinics, 'licensed_states') : null

  return (
    <ClinicCards item={item} clinics={clinics} fields={basicFields}>
      {diffLicensed && diffLicensed.size > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">Licensed States Differences</h4>
          <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            {presentClinics.map(clinic => (
              <div key={clinic.id} className="border rounded-lg p-3">
                <h5 className="text-xs font-semibold mb-2">{clinic.name}</h5>
                <StateGrid states={item.clinics[clinic.id]?.licensed_states} label="Licensed" color="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" differingStates={diffLicensed} />
              </div>
            ))}
          </div>
        </div>
      )}

      {!item.drift.has_drift && (
        <div className="text-xs text-muted-foreground">All config matches across clinics.</div>
      )}
    </ClinicCards>
  )
}
